import { useDispatch, useSelector } from 'react-redux';
import { ISpatStorageResource } from '../../../entities/farms.entities';
import { TBusinessType } from '../../../entities/general.entities';
import { translate } from '../../../lib/lang.helper';
import {
  SelectMusselSpatsByFarmID,
  SelectOysterSpatsByFarmID,
} from '../../../store/extra/extra.selector';
import { Button, Input, Subtitle } from '../../shared';
import { selectLang } from '../../../store/ui/ui.selector';
import { useState } from 'react';
import { showFeedback } from '../../../store/ui/ui.actions';
import { sendSingleRequest } from '../../../apis';
import { loadSpatStorages } from '../../../store/farms/farms.actions';
import {
  dozToPcs,
  getUnitOfSpecie,
  numToStr,
  pcsToDoz,
} from '../../../lib/common.helpers';
import { getSpatSourceLabel } from '../../../lib/farm.helpers';
import { selectAccount } from '../../../store/auth/auth.selector';

interface MusselProps {
  farmID?: number;
  spatStorageID: number;
  spatAmount: number;
  onClose: () => void;
}

const MusselSeedSpatUpdateForm = ({
  farmID,
  spatStorageID,
  spatAmount,
  onClose,
}: MusselProps) => {
  const lang = useSelector(selectLang);
  const dispatch = useDispatch<any>();
  const unitType = useSelector(selectAccount)?.display_unit;
  const spats = SelectMusselSpatsByFarmID(farmID);
  const batch = spats.find(x => x.id === spatStorageID) as ISpatStorageResource;

  const usedWeight = batch.weight - (batch.available_weight ?? 0);

  const [amount, setAmount] = useState<number | null>(spatAmount + usedWeight);
  const [disabled, setDisabled] = useState(false);

  const showError = (message: string) =>
    dispatch(
      showFeedback({
        isMessageModal: true,
        message,
        type: 'error',
      }),
    );
  const confirmClick = async () => {
    if (!amount) {
      showError(translate(lang, '_enter_valid_weight_batch'));
      return;
    }
    const metricAmount = amount ?? 0;
    if (metricAmount <= batch.weight) {
      showError(translate(lang, 'You should enter increased weight'));
      return;
    }
    setDisabled(true);
    const response = await sendSingleRequest(
      { ...batch, weight: metricAmount },
      'PUT',
      `api/farm/spat-storage/${batch.id}`,
      true,
    );
    if (response.status) {
      await dispatch(loadSpatStorages());
      dispatch(
        showFeedback({
          isMessageModal: true,
          message: 'Updated successfully, Please confirm your form again',
          type: 'success',
        }),
      );
      setDisabled(false);
      onClose();
    } else {
      showError(response.data?.message ?? 'Server error');
      setDisabled(false);
      return;
    }
  };

  return (
    <>
      <div className='wrap mb-32'>
        <div className='mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(
              lang,
              'You should increase the spat amount of %s',
              getSpatSourceLabel(spatStorageID, spats),
            )}
          </Subtitle>
          <div className='mt-12'>
            <Subtitle color='black-2' align='left' size={4} fontWeight={500}>
              {translate(
                lang,
                'Currently %s %s total, %s %s seeded, %s %s available',
                batch.weight,
                getUnitOfSpecie(unitType, 'weight_kg'),
                usedWeight,
                getUnitOfSpecie(unitType, 'weight_kg'),
                batch.available_weight,
                getUnitOfSpecie(unitType, 'weight_kg'),
              )}
            </Subtitle>
          </div>
        </div>
        <Input
          type='number'
          label={translate(lang, 'Spat amount')}
          unit={getUnitOfSpecie(unitType, 'weight_kg')}
          value={numToStr(amount)}
          onChange={e =>
            setAmount(
              e.target.value.length <= 0 ? null : Number(e.target.value),
            )
          }
        />
      </div>
      <div className='modal-button d-flex justify-end align-center'>
        <Button
          width={'small'}
          size={2}
          type='fill'
          color='red'
          className='rsp-btn ml-16'
          onClick={onClose}
          disabled={disabled}
        >
          {translate(lang, 'Go back')}
        </Button>
        <Button
          width={'small'}
          size={2}
          type='fill'
          color='green'
          className='rsp-btn ml-16'
          onClick={confirmClick}
          disabled={disabled}
        >
          {translate(lang, 'Confirm')}
        </Button>
      </div>
    </>
  );
};

const toOysterAmount = (unit: 'pcs' | 'dz', amount: number) =>
  unit === 'pcs' ? amount : Math.ceil(pcsToDoz(amount));

interface OysterProps {
  farmID?: number;
  spatStorageID: number;
  spatAmount: number;
  unit?: 'pcs' | 'dz';
  onClose: () => void;
}

const OysterSeedSpatUpdateForm = ({
  farmID,
  spatStorageID,
  spatAmount,
  unit = 'pcs',
  onClose,
}: OysterProps) => {
  const lang = useSelector(selectLang);
  const dispatch = useDispatch<any>();
  const spats = SelectOysterSpatsByFarmID(farmID);
  const batch = spats.find(x => x.id === spatStorageID) as ISpatStorageResource;
  const usedAmount = batch.weight - (batch.available_weight ?? 0);

  const [amount, setAmount] = useState<number | null>(
    spatAmount + toOysterAmount(unit, usedAmount),
  );
  const [disabled, setDisabled] = useState(false);

  const showError = (message: string) =>
    dispatch(
      showFeedback({
        isMessageModal: true,
        message,
        type: 'error',
      }),
    );
  const confirmClick = async () => {
    if (!amount) {
      showError(
        translate(
          lang,
          'Please enter valid %s of the batch',
          unit === 'dz' ? 'dozens' : 'pieces',
        ),
      );
      return;
    }
    const weight = unit === 'pcs' ? amount : dozToPcs(amount);

    if (weight <= batch.weight) {
      showError(
        translate(
          lang,
          'You should enter increased %s',
          unit === 'dz' ? 'dozens' : 'pieces',
        ),
      );
      return;
    }
    setDisabled(true);
    const response = await sendSingleRequest(
      { ...batch, weight },
      'PUT',
      `api/farm/spat-storage/${batch.id}`,
      true,
    );
    if (response.status) {
      await dispatch(loadSpatStorages());
      setDisabled(false);
      dispatch(
        showFeedback({
          isMessageModal: true,
          message: 'Updated successfully, Please confirm your form again',
          type: 'success',
        }),
      );
      onClose();
    } else {
      showError(response.data?.message ?? 'Server error');
      setDisabled(false);
      return;
    }
  };

  return (
    <>
      <div className='wrap mb-32'>
        <div className='mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(
              lang,
              'You should increase the spat amount of %s',
              getSpatSourceLabel(spatStorageID, spats),
            )}
          </Subtitle>
          <div className='mt-12'>
            <Subtitle color='black-2' align='left' size={4} fontWeight={500}>
              {translate(
                lang,
                'Currently %s %s total, %s %s seeded, %s %s available',
                toOysterAmount(unit, batch.weight),
                unit,
                toOysterAmount(unit, usedAmount),
                unit,
                toOysterAmount(unit, batch.available_weight ?? 0),
                unit,
              )}
            </Subtitle>
          </div>
        </div>
        <Input
          type='number'
          label={translate(lang, 'Spat amount')}
          unit={unit}
          value={numToStr(amount)}
          onChange={e =>
            setAmount(
              e.target.value.length <= 0 ? null : Number(e.target.value),
            )
          }
        />
      </div>
      <div className='modal-button d-flex justify-end align-center'>
        <Button
          width={'small'}
          size={2}
          type='fill'
          color='red'
          className='rsp-btn ml-16'
          onClick={onClose}
          disabled={disabled}
        >
          {translate(lang, 'Go back')}
        </Button>
        <Button
          width={'small'}
          size={2}
          type='fill'
          color='green'
          className='rsp-btn ml-16'
          onClick={confirmClick}
          disabled={disabled}
        >
          {translate(lang, 'Confirm')}
        </Button>
      </div>
    </>
  );
};

interface Props {
  type: TBusinessType;
  farmID: number;
  spatStorageID: number;
  spatAmount: number;
  unit?: 'pcs' | 'dz';
  onClose: () => void;
}

const SeedSpatUpdateForm = ({
  type,
  farmID,
  spatStorageID,
  spatAmount,
  unit,
  onClose,
}: Props) => (
  <>
    {type === 'MUSSEL' ? (
      <MusselSeedSpatUpdateForm
        farmID={farmID}
        spatStorageID={spatStorageID}
        spatAmount={spatAmount}
        onClose={onClose}
      />
    ) : type === 'OYSTER' ? (
      <OysterSeedSpatUpdateForm
        farmID={farmID}
        spatStorageID={spatStorageID}
        spatAmount={spatAmount}
        unit={unit}
        onClose={onClose}
      />
    ) : (
      <></>
    )}
  </>
);

export default SeedSpatUpdateForm;
